import { useCallback, type JSX } from 'react';
import { useFormContext, Controller, RegisterOptions, ControllerRenderProps, FieldValues } from 'react-hook-form';
import { ChevronDownIcon } from '@heroicons/react/24/outline';

import { Variable } from '@restworld/utility-types';
import { FormLabelWrapper, TextInput, RadioGroup } from '@restworld/ui-ds-base';

import { HelperTextRElement } from './form-inputs';
import { useWorkerApplicationCxt } from '../worker-application-wizard.context';

type WizardSelectFiledProps = {
  formLabel: string | JSX.Element;
  id: string;
  options?: Variable[];
  placeholder: string;
  mainTitle?: string;
  disabled?: boolean;
  rules?: RegisterOptions;
  errorMessage?: string;
  onChange?: (x: string) => void;
  subTitle?: string;
  testId?: string;
  inputTestId?: string;
};
export const WizardSelectField = ({
  options,
  placeholder,
  id,
  mainTitle,
  formLabel,
  disabled,
  rules,
  errorMessage,
  onChange,
  subTitle,
  testId,
  inputTestId,
}: WizardSelectFiledProps) => {
  const { control } = useFormContext();
  const { errorsTextMap, setOverlayModalContent, toggleApplicationOverlayModal } = useWorkerApplicationCxt();
  const onOptionSelect = useCallback(
    (value: string) => {
      toggleApplicationOverlayModal(false);
      !!onChange && onChange(value);
    },
    [onChange, toggleApplicationOverlayModal]
  );
  const handleClick = useCallback(
    (field: ControllerRenderProps<FieldValues, string>) =>
      setOverlayModalContent({
        visible: true,
        content: (
          <RadioGroup
            id={id}
            isFullWidth
            options={options || []}
            mainTitle={mainTitle || formLabel}
            subTitle={subTitle}
            {...{
              ...field,
              onChange: (value: string) => {
                field.onChange(value);
                onOptionSelect(value);
              },
            }}
            testId={testId}
          />
        ),
      }),
    [formLabel, id, mainTitle, onOptionSelect, options, setOverlayModalContent, subTitle, testId]
  );
  return (
    <FormLabelWrapper disabled={disabled} id={id} label={formLabel}>
      <Controller
        name={id}
        control={control}
        rules={rules}
        render={({ field }) => (
          <TextInput
            id={id}
            placeholder={placeholder}
            type="text"
            rightIcon={ChevronDownIcon}
            helperText={<HelperTextRElement helperText={errorMessage || errorsTextMap[id]} />}
            color={errorMessage || errorsTextMap[id] ? 'failure' : undefined}
            readOnly
            disabled={disabled}
            {...field}
            value={options?.find((v) => v.id === field.value)?.label || ''}
            onClick={() => handleClick(field)}
            testId={inputTestId}
          />
        )}
      />
    </FormLabelWrapper>
  );
};
